/**
 * User API Service
 * All user operations go through backend API endpoints
 */

import { API_BASE_URL } from '@/constants/config';
import { logger } from '@/lib/logger';
import { getNexusToken, clearNexusToken } from '@/utils/authToken';

export interface UserProfile {
  id: string;
  rfc: string | null;
  fiscal_name: string | null;
  trade_name: string | null;
  email: string;
  phone: string | null;
  address: string | null;
  type: 'flotillero' | 'independiente';
  status: string;
  email_verified: boolean;
  whatsapp_verified: boolean;
  whatsapp_verified_at: string | null;
  whatsapp_expired?: boolean;
  bank_name: string | null;
  bank_clabe: string | null;
  bank_institution_id: string | null;
  onboarding_completed: boolean;
  requires_password_change: boolean;
  last_login_at: string | null;
  created_at: string;
  stats?: {
    drivers_count: number;
    invoices_total: number;
    invoices_paid: number;
    invoices_pending: number;
    total_facturado: number;
  };
}

export interface LoginResponse {
  token: string;
  user: UserProfile;
}

export interface OnboardingStatus {
  currentStep: string;
  steps: {
    verify_email: { completed: boolean; required: boolean };
    verify_whatsapp: { completed: boolean; required: boolean; expired: boolean };
    bank_info: { completed: boolean; required: boolean };
    profile_info: { completed: boolean; required: boolean };
    upload_csd: { completed: boolean; required: boolean; skipped: boolean };
    change_password: { completed: boolean; required: boolean };
  };
  isComplete: boolean;
  canComplete: boolean;
  user: {
    email: string;
    email_verified: boolean;
    whatsapp_verified: boolean;
    whatsapp_verified_at: string | null;
    rfc: string | null;
    fiscal_name: string | null;
    fiscal_regime_code: string | null;
    fiscal_zip_code: string | null;
    phone: string | null;
    address: string | null;
    bank_name: string | null;
    bank_clabe: string | null;
    requires_password_change: boolean;
  };
}

export interface UserInvoice {
  id: string;
  uuid: string;
  folio: string | null;
  invoice_date: string;
  total_amount: number;
  net_payment_amount: number | null;
  payment_program: string;
  pronto_pago_fee_amount: number | null;
  payment_week: number;
  payment_year: number;
  status: string;
  created_at: string;
  project_name: string | null;
  project_code: string | null;
  is_late?: boolean;
  late_reason?: string | null;
}

export interface InvoicesResponse {
  invoices: UserInvoice[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  summary: {
    total_facturado: number;
    total_pendiente: number;
    total_pagado: number;
    count_pending: number;
    count_paid: number;
  };
}

// ========== Token Management ==========

export function setUserToken(token: string | null) {
  if (token) {
    localStorage.setItem('nexus_token', token);
  } else {
    clearNexusToken();
  }
}

export function getStoredUserToken(): string | null {
  return getNexusToken() || null;
}

// ========== API Request Helper ==========

async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<{ success: boolean; data?: T; message: string; error?: string; details?: string[] }> {
  const token = getStoredUserToken();

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
      credentials: 'include',
    });

    const data = await response.json();

    if (response.status === 401 && token) {
      clearNexusToken();
      window.location.href = '/app/login';
      return {
        success: false,
        data: data.data,
        message: data.message || 'Sesión expirada',
        error: data.error || 'UNAUTHORIZED',
        details: data.details,
      };
    }

    if (!response.ok) {
      return {
        success: false,
        data: data.data,
        message: data.message || 'Error en la solicitud',
        error: data.error,
        details: data.details,
      };
    }

    return {
      success: true,
      data: data.data,
      message: data.message || 'OK',
    };
  } catch (error) {
    logger.error('API request error', { error });
    return {
      success: false,
      message: 'Error de conexión',
      error: 'NETWORK_ERROR',
    };
  }
}

// ========== Authentication ==========

export async function userLogin(
  email: string,
  password: string
): Promise<{
  success: boolean;
  user?: UserProfile;
  message: string;
  error?: string;
  lockedUntil?: string;
  remainingMinutes?: number;
}> {
  const result = await apiRequest<LoginResponse>('/api/user/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });

  if (result.success && result.data) {
    setUserToken(result.data.token);
    return {
      success: true,
      user: result.data.user,
      message: result.message,
    };
  }

  // Parse locked account data from the error response
  if (result.error === 'ACCOUNT_LOCKED') {
    const lockData = (result as Record<string, unknown>).data as
      | { lockedUntil?: string; remainingMinutes?: number }
      | undefined;
    return {
      success: false,
      message: result.message,
      error: 'ACCOUNT_LOCKED',
      lockedUntil: lockData?.lockedUntil,
      remainingMinutes: lockData?.remainingMinutes,
    };
  }

  return {
    success: false,
    message: result.message,
    error: result.error,
  };
}

export async function requestAccountUnlock(
  email: string
): Promise<{ success: boolean; message: string }> {
  return apiRequest('/api/user/request-unlock', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export async function verifyUnlockToken(
  token: string
): Promise<{ success: boolean; message: string }> {
  return apiRequest('/api/user/unlock', {
    method: 'POST',
    body: JSON.stringify({ token }),
  });
}

export async function userLogout(): Promise<void> {
  // No need to call server for logout, just clear token
  setUserToken(null);
}

export async function checkUserSession(): Promise<{
  isValid: boolean;
  user?: UserProfile;
}> {
  const token = getStoredUserToken();
  if (!token) {
    return { isValid: false };
  }

  const result = await apiRequest<UserProfile>('/api/user/profile');

  if (result.success && result.data) {
    return {
      isValid: true,
      user: result.data,
    };
  }

  // Token invalid, clear it
  setUserToken(null);
  return { isValid: false };
}

// ========== Identify-First Auth ==========

export type IdentifyStatus = 'login' | 'onboarding' | 'register';

export interface IdentifyResponse {
  status: IdentifyStatus;
  displayName?: string;
}

export async function identifyUser(
  email: string
): Promise<{ success: boolean; data?: IdentifyResponse; message: string }> {
  return apiRequest<IdentifyResponse>('/api/user/identify', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export async function activateUser(
  email: string,
  password: string,
  confirmPassword: string
): Promise<{ success: boolean; user?: UserProfile; message: string }> {
  const result = await apiRequest<LoginResponse>('/api/user/activate', {
    method: 'POST',
    body: JSON.stringify({ email, password, confirmPassword }),
  });

  if (result.success && result.data) {
    setUserToken(result.data.token);
    return {
      success: true,
      user: result.data.user,
      message: result.message,
    };
  }

  return {
    success: false,
    message: result.message,
  };
}

export async function registerUser(
  email: string,
  password: string,
  rfc: string
): Promise<{ success: boolean; user?: UserProfile; message: string }> {
  const result = await apiRequest<LoginResponse>('/api/user/register', {
    method: 'POST',
    body: JSON.stringify({ email, password, rfc }),
  });

  if (result.success && result.data) {
    setUserToken(result.data.token);
    return {
      success: true,
      user: result.data.user,
      message: result.message,
    };
  }

  return {
    success: false,
    message: result.message,
  };
}

// ========== Profile ==========

export async function getUserProfile(): Promise<{
  success: boolean;
  data?: UserProfile;
  message: string;
}> {
  return apiRequest<UserProfile>('/api/user/profile');
}

export async function updateUserProfile(data: {
  phone?: string;
  address?: string;
  trade_name?: string;
  bank_name?: string;
  bank_clabe?: string;
  bank_institution_id?: string;
}): Promise<{
  success: boolean;
  data?: Partial<UserProfile>;
  message: string;
}> {
  return apiRequest('/api/user/profile', {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// ========== Onboarding ==========

export async function getOnboardingStatus(): Promise<{
  success: boolean;
  data?: OnboardingStatus;
  message: string;
}> {
  return apiRequest<OnboardingStatus>('/api/user/onboarding');
}

export async function updateOnboardingBank(data: {
  bank_name: string;
  bank_clabe: string;
  bank_account_number?: string;
  bank_institution_id?: string;
}): Promise<{
  success: boolean;
  message: string;
}> {
  return apiRequest('/api/user/onboarding?step=bank', {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function updateOnboardingProfile(data: {
  rfc: string;
  fiscal_name: string;
  phone: string;
  address: string;
  trade_name?: string;
}): Promise<{
  success: boolean;
  message: string;
}> {
  return apiRequest('/api/user/onboarding?step=profile', {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function updateOnboardingPassword(data: {
  current_password: string;
  new_password: string;
}): Promise<{
  success: boolean;
  message: string;
}> {
  return apiRequest('/api/user/onboarding?step=password', {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function completeOnboarding(): Promise<{
  success: boolean;
  message: string;
}> {
  return apiRequest('/api/user/onboarding', {
    method: 'POST',
  });
}

export async function skipCSDOnboarding(): Promise<{
  success: boolean;
  message: string;
}> {
  return apiRequest('/api/user/onboarding?step=csd_skip', {
    method: 'PUT',
  });
}

// ========== Invoices ==========

export interface InvoiceFilters {
  page?: number;
  pageSize?: number;
  status?: string;
  week?: number;
  year?: number;
  search?: string;
}

export async function getUserInvoices(filters: InvoiceFilters = {}): Promise<{
  success: boolean;
  data?: InvoicesResponse;
  message: string;
}> {
  const params = new URLSearchParams();

  if (filters.page) params.set('page', filters.page.toString());
  if (filters.pageSize) params.set('pageSize', filters.pageSize.toString());
  if (filters.status) params.set('status', filters.status);
  if (filters.week) params.set('week', filters.week.toString());
  if (filters.year) params.set('year', filters.year.toString());
  if (filters.search) params.set('search', filters.search);

  return apiRequest<InvoicesResponse>(`/api/user/invoices?${params.toString()}`);
}

// ========== Magic Link ==========

export async function requestMagicLink(email: string): Promise<{
  success: boolean;
  message: string;
}> {
  return apiRequest('/api/user/magic-link', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export async function verifyMagicLink(token: string): Promise<{
  success: boolean;
  user?: UserProfile;
  message: string;
}> {
  const result = await apiRequest<LoginResponse>(`/api/user/verify-magic-link?token=${token}`);

  if (result.success && result.data) {
    setUserToken(result.data.token);
    return {
      success: true,
      user: result.data.user,
      message: result.message,
    };
  }

  return {
    success: false,
    message: result.message,
  };
}

// ========== Email Verification ==========

export async function sendVerificationCode(): Promise<{
  success: boolean;
  message: string;
  data?: { alreadyVerified?: boolean; expiresMinutes?: number };
}> {
  return apiRequest('/api/user/verify-email', {
    method: 'POST',
  });
}

export async function verifyEmailCode(code: string): Promise<{
  success: boolean;
  message: string;
  data?: { emailVerified?: boolean };
  error?: string;
}> {
  return apiRequest('/api/user/verify-email', {
    method: 'PUT',
    body: JSON.stringify({ code }),
  });
}

// ========== WhatsApp Verification ==========

export async function sendWhatsAppVerificationCode(phone: string): Promise<{
  success: boolean;
  message: string;
  data?: { alreadyVerified?: boolean; expiresMinutes?: number };
}> {
  return apiRequest('/api/user/verify-whatsapp', {
    method: 'POST',
    body: JSON.stringify({ phone }),
  });
}

export async function verifyWhatsAppCode(code: string): Promise<{
  success: boolean;
  message: string;
  data?: { whatsappVerified?: boolean };
  error?: string;
}> {
  return apiRequest('/api/user/verify-whatsapp', {
    method: 'PUT',
    body: JSON.stringify({ code }),
  });
}

// ========== Password Reset ==========

export async function requestPasswordReset(email: string): Promise<{
  success: boolean;
  message: string;
}> {
  return apiRequest('/api/user/forgot-password', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export async function resetPassword(
  email: string,
  code: string,
  newPassword: string
): Promise<{
  success: boolean;
  message: string;
}> {
  return apiRequest('/api/user/forgot-password', {
    method: 'PUT',
    body: JSON.stringify({ email, code, newPassword }),
  });
}
